import type { ComponentProps } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { cn } from "../lib/utils";

export type CalendarProps = ComponentProps<typeof DayPicker>;

const navButtonClass = cn(
  "inline-flex size-7 items-center justify-center rounded-crane-sm text-crane-muted-foreground transition-colors",
  "hover:bg-crane-accent hover:text-crane-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crane-ring focus-visible:ring-offset-2 focus-visible:ring-offset-crane-background",
  "disabled:pointer-events-none disabled:opacity-40"
);

const Calendar = ({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) => (
  <DayPicker
    showOutsideDays={showOutsideDays}
    className={cn("p-3", className)}
    classNames={{
      root: "text-sm text-crane-foreground",
      months: "relative flex flex-col gap-4",
      month: "flex w-full flex-col gap-2.5",
      month_caption: "flex h-7 items-center justify-center",
      caption_label: "text-crane-xl font-semibold text-crane-foreground",
      nav: "absolute inset-x-0 top-0 z-10 flex h-7 items-center justify-between",
      button_previous: navButtonClass,
      button_next: navButtonClass,
      month_grid: "w-full border-collapse",
      weekdays: "flex",
      weekday: "flex size-8 items-center justify-center text-crane-xs font-semibold uppercase tracking-crane-label text-crane-muted-foreground",
      weeks: "mt-1 flex flex-col gap-0.5",
      week: "flex",
      day: "p-0 text-center",
      day_button: cn(
        "flex size-8 items-center justify-center rounded-crane-sm text-xs transition-colors",
        "hover:bg-crane-accent",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-crane-ring"
      ),
      selected: "rounded-crane-sm bg-crane-primary text-crane-primary-foreground [&>button]:hover:bg-crane-primary",
      today: "font-semibold text-crane-primary",
      outside: "text-crane-muted-foreground opacity-60",
      disabled: "text-crane-muted-foreground opacity-40",
      hidden: "invisible",
      ...classNames,
    }}
    components={{
      Chevron: ({ orientation }) =>
        orientation === "left" ? (
          <ChevronLeft className="size-4" aria-hidden />
        ) : (
          <ChevronRight className="size-4" aria-hidden />
        ),
    }}
    {...props}
  />
);

export { Calendar };
